import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MessageCircle, MapPin, Clock, Tag } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import LeadSection from "@/components/home/LeadSection";
import { BlogCard } from "@/components/blog/BlogCard";
import { DistrictRecommender } from "@/components/blog/DistrictRecommender";
import { getBlog } from "@/lib/api";
import { useSettings, waLink, SITE_ORIGIN } from "@/lib/site";
import { rememberDistrict } from "@/lib/districtPreference";

// Internal markdown links (/paketler, /blog/...) should use client-side routing
const MdLink = ({ href, children }) => {
  if (href && href.startsWith("/")) {
    return <Link to={href}>{children}</Link>;
  }
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
};

export default function BlogDetail() {
  const { slug } = useParams();
  const settings = useSettings();
  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["blog", slug],
    queryFn: () => getBlog(slug),
    retry: 1,
  });

  // remember the reader's district when they read a district guide
  useEffect(() => {
    if (post && post.district_slug) rememberDistrict(post.district_slug);
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0E0F10] pt-28 sm:pt-32 lg:pt-40" data-testid="blog-detail-loading">
        <div className="container-lux max-w-3xl space-y-4">
          <Skeleton className="h-4 w-40 bg-white/5" />
          <Skeleton className="h-12 w-full bg-white/5" />
          <Skeleton className="h-12 w-3/4 bg-white/5" />
          <Skeleton className="h-6 w-full bg-white/5 mt-6" />
          <Skeleton className="h-[420px] w-full rounded-3xl bg-white/5 mt-10" />
        </div>
      </div>
    );
  }
  if (isError || !post) {
    return (
      <div className="min-h-screen grid place-items-center text-center px-4" data-testid="blog-detail-error">
        <div>
          <h1 className="font-display text-3xl text-[#F7F2E8]">Yazı bulunamadı</h1>
          <Link to="/blog" className="text-[#D7B86A] mt-4 inline-block">Tüm yazılar</Link>
        </div>
      </div>
    );
  }

  const isDistrict = Boolean(post.district_slug);
  const categoryHref = post.category ? `/blog?kategori=${post.category}` : "/blog";

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: post.title, image: post.hero_image,
      description: post.meta_description, inLanguage: "tr-TR",
      articleSection: post.category_name || undefined,
      keywords: (post.keywords || []).join(", ") || undefined,
      datePublished: post.created_at, dateModified: post.updated_at || post.created_at,
      author: { "@type": "Organization", name: settings.brand_name },
      publisher: { "@type": "Organization", name: settings.brand_name },
      mainEntityOfPage: `${SITE_ORIGIN}/blog/${post.slug}`,
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Anasayfa", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_ORIGIN}/blog` },
        ...(post.category_name
          ? [{ "@type": "ListItem", position: 3, name: post.category_name, item: `${SITE_ORIGIN}${categoryHref}` }]
          : []),
        { "@type": "ListItem", position: post.category_name ? 4 : 3, name: post.title },
      ],
    },
  ];
  if (post.faq?.length) {
    jsonLd.push({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: post.faq.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
    });
  }

  const ctaTitle = isDistrict
    ? `${post.district_name}'de çekim mi planlıyorsunuz?`
    : "Düğün çekiminizi planlıyor musunuz?";
  const waMessage = isDistrict
    ? `Merhaba, ${post.district_name} için düğün çekimi hakkında bilgi almak istiyorum.`
    : `Merhaba, "${post.title}" yazınızı okudum; düğün çekimi hakkında bilgi almak istiyorum.`;

  return (
    <>
      <Seo title={`${post.title}`} description={post.meta_description} path={`/blog/${post.slug}`}
        image={post.hero_image} type="article" jsonLd={jsonLd} />

      <article data-testid="blog-article">
        {/* hero */}
        <section className="relative pt-28 sm:pt-32 lg:pt-40 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0F10] via-[#0E0F10]/85 to-[#0E0F10]/55" />
          </div>
          <div className="container-lux relative max-w-3xl">
            <nav className="text-sm text-[#B8B1A5] flex flex-wrap items-center gap-2" aria-label="breadcrumb" data-testid="blog-breadcrumb">
              <Link to="/blog" className="hover:text-white">Blog</Link>
              {post.category_name && (
                <>
                  <span>/</span>
                  <Link to={categoryHref} className="hover:text-white" data-testid="blog-breadcrumb-category">{post.category_name}</Link>
                </>
              )}
              {isDistrict && (
                <>
                  <span>/</span>
                  <span className="text-[#D7B86A]">{post.district_name}</span>
                </>
              )}
            </nav>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5">
              {isDistrict && post.district ? (
                <Link to={`/ankara/${post.district.page_slug}`} data-testid="blog-district-link"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-[#C8A24A] hover:text-[#D7B86A]">
                  <MapPin className="w-3.5 h-3.5" /> {post.district_name}, Ankara
                </Link>
              ) : (
                post.category_name && (
                  <Link to={categoryHref} data-testid="blog-category-badge"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-[#C8A24A] hover:text-[#D7B86A]">
                    <Tag className="w-3.5 h-3.5" /> {post.category_name}
                  </Link>
                )
              )}
              {post.reading_minutes ? (
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-[#9A9387]" data-testid="blog-reading-time">
                  <Clock className="w-3.5 h-3.5" /> {post.reading_minutes} dk okuma
                </span>
              ) : null}
            </div>

            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F7F2E8] mt-3 leading-[1.1]" data-testid="blog-title">{post.title}</h1>
            <p className="text-[#CFC6B8] mt-4 text-lg">{post.excerpt}</p>
          </div>
        </section>

        {/* content on cream surface */}
        <section className="bg-[#0E0F10] pb-8">
          <div className="container-lux max-w-3xl">
            <div className="rounded-3xl bg-[#FBF7EF] p-6 sm:p-10 border border-black/5 -mt-6">
              <div className="lux-prose" data-testid="blog-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MdLink }}>{post.content_markdown}</ReactMarkdown>
              </div>

              {/* mid CTA */}
              <div className="mt-8 rounded-2xl bg-[#141517] p-6 text-center">
                <h3 className="font-display text-2xl text-[#F7F2E8]" data-testid="blog-cta-title">{ctaTitle}</h3>
                <p className="text-[#9A9387] text-sm mt-2">Uygun tarihleri ve paket fiyatlarını hemen öğrenin.</p>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                  <a href={waLink(settings, waMessage)}
                    target="_blank" rel="noreferrer" data-testid="blog-whatsapp-cta" className="inline-block">
                    <Button className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold">
                      <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp'tan Yaz
                    </Button>
                  </a>
                  <Link to="/paketler" data-testid="blog-packages-cta">
                    <Button variant="outline" className="border-white/20 text-[#F7F2E8] bg-transparent hover:bg-white/5">
                      Paketleri İncele
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* smart district box for general posts */}
        {!isDistrict && (
          <section className="bg-[#0E0F10] pb-6">
            <div className="container-lux max-w-3xl">
              <DistrictRecommender contentText={`${post.title} ${post.content_markdown || ""}`} />
            </div>
          </section>
        )}

        {/* FAQ */}
        {post.faq?.length > 0 && (
          <section className="section-pad bg-[#0E0F10]">
            <div className="container-lux max-w-3xl">
              <h2 className="font-display text-3xl text-[#F7F2E8] text-center mb-8">Sık Sorulan Sorular</h2>
              <Accordion type="single" collapsible data-testid="blog-faq">
                {post.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} className="border-white/10">
                    <AccordionTrigger className="text-left text-[#EDE7DA] hover:text-white py-5">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-[#9A9387] leading-relaxed">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* related */}
        {post.related?.length > 0 && (
          <section className="pb-20 bg-[#0E0F10]" data-testid="blog-related">
            <div className="container-lux">
              <div className="flex items-end justify-between gap-4 mb-6">
                <h2 className="font-display text-2xl text-[#F7F2E8]">
                  {isDistrict ? "Yakın ilçe rehberleri" : `${post.category_name || "İlgili"} yazıları`}
                </h2>
                <Link to={categoryHref} className="text-sm text-[#D7B86A] hover:text-white">Tümünü gör</Link>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                {post.related.map((r) => (
                  <BlogCard key={r.slug} post={r} compact />
                ))}
              </div>
            </div>
          </section>
        )}
      </article>

      <LeadSection presetDistrict={post.district_slug || ""} />
    </>
  );
}
